import { isEmpty, isNil, without } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchInput from '@/components/common/search-input';
import { UserCarousel, type UserCarouselItem } from '@/components/common/user-carousel/user-carousel';
import useSearchFriends from '@/hooks/search/use-search-friends';
import useUserInfo from '@/hooks/users/use-user-info';
import { getUserId } from '@/stores/auth';
import ContactList from './contact-list';

interface Props {
  selectedIds?: number[];
  // ids cannot be selected
  disabledIds?: number[];
  // ids should be selected and cannot unselect
  residentIds?: number[];

  onSelect?: (friendIds: number[]) => void;
  disableRemove?: boolean;
  maxSelectCount?: number;
  showOnlineDesc?: boolean;
  canSelectAll?: boolean;
}

function SelectContactList({
  onSelect,
  selectedIds = [],
  disabledIds,
  residentIds,
  disableRemove,
  maxSelectCount,
  showOnlineDesc = true,
  canSelectAll = true,
}: Props) {
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo();
  const [searchTerm, setSearchTerm] = useState('');
  const { friends, filteredFriends } = useSearchFriends(searchTerm);

  const selectedFriends = useMemo(() => {
    const selfId = getUserId();
    return selectedIds
      .map((userId) => {
        if (userId === selfId)
          return {
            userId: selfId,
            avatarUrl: userInfo?.HeadImg,
            label: userInfo?.NickName || '',
          };
        const member = friends.find((f) => f.FriendID === userId);

        return member
          ? ({
              userId: member.FriendID,
              avatarUrl: member.FriendHeadImg,
              label: member.RemarkName || member.FriendNickName,
            } satisfies UserCarouselItem)
          : undefined;
      })
      .filter((v) => !isNil(v));
  }, [selectedIds, friends, userInfo]);

  return (
    <>
      {!isEmpty(selectedIds) && (
        <div className='ml-5'>
          <UserCarousel
            users={selectedFriends}
            disableRemoveUserIds={disabledIds}
            onRemove={disableRemove ? undefined : (userId) => onSelect?.(without(selectedIds, userId))}
          />
        </div>
      )}

      <SearchInput
        keyword={searchTerm}
        setKeyword={setSearchTerm}
        className='mx-5 mb-4 mt-0 desktop:mx-0'
        clearKeyword={searchTerm ? () => setSearchTerm?.('') : undefined}
      />

      <ContactList
        canSelectAll={canSelectAll}
        friends={filteredFriends}
        maxCount={maxSelectCount}
        selectedIds={selectedIds}
        disabledIds={disabledIds}
        residentIds={residentIds}
        onSelect={onSelect}
        showOnlineDesc={showOnlineDesc}
      />
    </>
  );
}

export default SelectContactList;
